import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Typography, Stack, InputAdornment, Switch } from '@mui/material'
import noop from 'lodash/noop'
import Dinero from 'dinero.js'
import rem from 'utils/rem'
import { useGetBenchmarksQuery } from 'services/benchmarks'
import { useGetMaterialTypeQuery } from 'services/materialTypes'
import { PRICE_TYPES, MEASURE_UNIT_ARRAY } from 'utils/constants'
import Select from 'components/generic/form/Select'
import Input from 'components/generic/form/Input'
import BenchmarkAutocomplete from 'components/generic/form/AsyncAutocomplete/BenchmarkAutocomplete'
import { setDecimalPrecision, unformatStringNumber } from 'utils/helpers'
import PricingTableCell from './PricingTableCell'

const calculateFormulaPrice = (selectedBenchmark, differential, recovery, otherCosts) => {
  if (selectedBenchmark) {
    const benchmarkPriceValue = unformatStringNumber(Dinero({ ...selectedBenchmark?.price }).toUnit()) || 0
    const differntialValue = unformatStringNumber(differential) || 0
    const recoveryPercentValue = unformatStringNumber(recovery) || 0
    const otherCostsValue = unformatStringNumber(otherCosts) || 0

    return setDecimalPrecision(
      ((benchmarkPriceValue - differntialValue) * recoveryPercentValue) / 100 - otherCostsValue,
      { shouldFormatNumber: false },
    )
  }

  return setDecimalPrecision(0)
}

const PricingFormulaRow = ({
  isViewMode,
  control,
  watch,
  isBuy,
  setValue,
  tradePrice,
  priceEntryUnits,
  materialTypeId,
  isPricelist,
}) => {
  const { data: benchmarksData, isLoading: isLoadingBenchmarksData } = useGetBenchmarksQuery({})
  const { data: materialTypeData } = useGetMaterialTypeQuery({ materialTypeId }, { skip: !materialTypeId })

  const { decimalPrecision: decimalPrecisionSetting = 2, disableUmDropdowns } = useSelector(
    state => state.settings?.settings?.purchaseSettings?.inboundTickets || {},
  )
  const trade = isBuy ? 'buy' : 'sell'

  const [inputDisabled, setInputDisabled] = useState(false)
  const [isFormula, setIsFormula] = useState(false)
  const [referenceName, setReferenceName] = useState('')
  const [selectedBenchmark, setSelectedBenchmark] = useState(null)
  const formulaOption = watch(`pricing.${trade}Formula`)

  const differential = unformatStringNumber(watch(`pricing.${trade}Differential`))
  const recovery = unformatStringNumber(watch(`pricing.${trade}Recovery`))
  const otherCosts = unformatStringNumber(watch(`pricing.${trade}OtherCosts`))
  const price = unformatStringNumber(watch(`pricing.${trade}Price`))

  useEffect(() => {
    const tradePriceIsFormula = tradePrice?.priceType === PRICE_TYPES.FORMULA.id
    setIsFormula(tradePriceIsFormula)
    setValue(`pricing.${trade}Formula`, tradePriceIsFormula ? PRICE_TYPES.FORMULA.id : PRICE_TYPES.SPOT.id)

    if (tradePriceIsFormula) {
      if (tradePrice?.benchmarkId) {
        const benchmarkData = benchmarksData?.Items?.find(item => item.id === tradePrice?.benchmarkId)
        setValue(`pricing.${trade}Benchmark`, benchmarkData)
        setSelectedBenchmark(benchmarkData)
        setReferenceName(benchmarkData?.name)
      } else if (tradePrice?.referencePriceEntryId) {
        const referenceIsBuy = tradePrice?.referencePriceEntryId === materialTypeData?.defaultPriceEntry?.buyPrice?.id
        const referenceBenchmark = referenceIsBuy
          ? { ...materialTypeData?.defaultPriceEntry?.buyPrice, name: 'Default Buy' }
          : { ...materialTypeData?.defaultPriceEntry?.sellPrice, name: 'Default Sell' }
        setValue(`pricing.${trade}Benchmark`, referenceBenchmark)
        setSelectedBenchmark(referenceBenchmark)
        setReferenceName(`${referenceIsBuy ? 'Buy' : 'Sell'} Price`)
      }
    } else {
      setValue(`pricing.${trade}Benchmark`, null)
    }

    setValue(
      `pricing.${trade}Differential`,
      setDecimalPrecision(Dinero({ ...tradePrice?.differential }).toUnit(), { shouldFormatNumber: false }),
    )
    setValue(`pricing.${trade}Recovery`, tradePrice?.recoveryPercentage || 100)
    setValue(
      `pricing.${trade}OtherCosts`,
      setDecimalPrecision(Dinero({ ...tradePrice?.otherCosts }).toUnit(), { shouldFormatNumber: false }),
    )
    setValue(
      `pricing.${trade}Price`,
      setDecimalPrecision(Dinero({ ...tradePrice?.price }).toUnit(), { shouldFormatNumber: false }),
    )
    setValue(`pricing.${trade}Unit`, priceEntryUnits)
    setValue(`pricing.${trade}MinMaxRange`, tradePrice?.minMaxRange || 0)
  }, [tradePrice, benchmarksData, materialTypeData])

  useEffect(() => {
    if (formulaOption === PRICE_TYPES.FORMULA.id) {
      const formulaPrice = calculateFormulaPrice(selectedBenchmark, differential, recovery, otherCosts)
      setValue(`pricing.${trade}Price`, formulaPrice)
    }
  }, [formulaOption, selectedBenchmark, differential, recovery, otherCosts, price])

  useEffect(() => {
    if (!isViewMode && formulaOption && formulaOption !== PRICE_TYPES.FORMULA.id) {
      setValue(`pricing.${trade}Benchmark`, null)
      setValue(`pricing.${trade}Differential`, 0)
      setValue(`pricing.${trade}Recovery`, 100)
      setValue(`pricing.${trade}OtherCosts`, 0)
      setValue(
        `pricing.${trade}Price`,
        setDecimalPrecision(Dinero({ ...tradePrice?.price }).toUnit(), { shouldFormatNumber: false }),
      )
    }
  }, [isViewMode, formulaOption])

  useEffect(() => {
    setInputDisabled(formulaOption !== PRICE_TYPES.FORMULA.id)
  }, [formulaOption])

  const handleToggle = () => {
    setIsFormula(wasFormula => {
      setValue(`pricing.${trade}Formula`, PRICE_TYPES[wasFormula ? 'SPOT' : 'FORMULA'].id, { shouldDirty: true })
      return !wasFormula
    })
  }

  return (
    <tr style={{ borderBottom: '1px solid grey' }} data-testid={`${trade}-price`}>
      <PricingTableCell width="10%" textAlign="center">
        <Typography fontSize={rem(13)}>
          {isPricelist ? null : 'Default '} {isBuy ? 'Buy' : 'Sell'}
        </Typography>
      </PricingTableCell>
      <PricingTableCell width="11%" textAlign="center" data-testid="formula-toggle">
        <Switch checked={isFormula} onChange={handleToggle} disabled={isViewMode} />
      </PricingTableCell>
      <PricingTableCell width="20%" textAlign="left">
        <Stack direction="row" alignItems="center" spacing={1}>
          {isViewMode ? (
            <Typography fontSize={rem(14)}>{referenceName}</Typography>
          ) : (
            <BenchmarkAutocomplete
              name={`pricing.${trade}Benchmark`}
              label=""
              data-test={`pricing-${trade}Benchmark`}
              variant="outlined"
              defaultValue={null}
              control={control}
              freeSolo={false}
              disabled={inputDisabled}
              selectedBenchmark={selectedBenchmark}
              setSelectedBenchmark={setSelectedBenchmark}
              benchmarksData={benchmarksData}
              isLoadingBenchmarksData={isLoadingBenchmarksData}
              materialTypeData={materialTypeData}
              size="small"
              isBuy={isBuy}
              tradePrice={tradePrice}
              isPricelist={isPricelist}
              InputProps={{
                style: { fontSize: '13px' },
              }}
            />
          )}
          <Typography fontSize={rem(14)}>
            ${setDecimalPrecision(Dinero({ ...selectedBenchmark?.price }).toUnit())}
          </Typography>
        </Stack>
      </PricingTableCell>
      <PricingTableCell width="10%" textAlign="center">
        {isViewMode ? (
          <Typography fontSize={rem(14)}>
            ${setDecimalPrecision(Dinero({ ...tradePrice?.differential }).toUnit())}
          </Typography>
        ) : (
          <Input
            name={`pricing.${trade}Differential`}
            data-test={`pricing-${trade}Differential`}
            type="number"
            pattern="[0-9]*"
            defaultValue={0.0}
            control={control}
            fractionDigits={decimalPrecisionSetting}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { height: '40px', fontSize: '13px' },
              inputProps: {
                onFocus: event => setTimeout(() => event.target.focus(), 100),
                step: 0.01,
                min: 0.0,
              },
              disabled: inputDisabled,
            }}
          />
        )}
      </PricingTableCell>
      <PricingTableCell width="9%" textAlign="center">
        {isViewMode ? (
          <Typography fontSize={rem(14)}>
            {tradePrice?.recoveryPercentage ? `${tradePrice?.recoveryPercentage} %` : '100 %'}
          </Typography>
        ) : (
          <Input
            name={`pricing.${trade}Recovery`}
            data-test={`pricing-${trade}Recovery`}
            type="number"
            pattern="[0-9]*"
            defaultValue={100}
            control={control}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              sx: { height: '40px', fontSize: '13px' },
              inputProps: {
                onFocus: event => setTimeout(() => event.target.focus(), 100),
                min: 0,
                max: 100,
              },
              disabled: inputDisabled,
            }}
          />
        )}
      </PricingTableCell>
      <PricingTableCell width="10%" textAlign="center">
        {isViewMode ? (
          <Typography fontSize={rem(14)}>
            ${setDecimalPrecision(Dinero({ ...tradePrice?.otherCosts }).toUnit())}
          </Typography>
        ) : (
          <Input
            name={`pricing.${trade}OtherCosts`}
            data-test={`pricing-${trade}OtherCosts`}
            type="number"
            pattern="[0-9]*"
            defaultValue={0.0}
            control={control}
            fractionDigits={decimalPrecisionSetting}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { height: '40px', fontSize: '13px' },
              inputProps: {
                onFocus: event => setTimeout(() => event.target.focus(), 100),
                step: 0.01,
                min: 0.0,
              },
              disabled: inputDisabled,
            }}
          />
        )}
      </PricingTableCell>
      <PricingTableCell width="10%" textAlign="left">
        {isViewMode ? (
          <Typography fontSize={rem(14)}>${setDecimalPrecision(Dinero({ ...tradePrice?.price }).toUnit())}</Typography>
        ) : (
          <Input
            name={`pricing.${trade}Price`}
            data-test={`pricing-${trade}Price`}
            type="number"
            pattern="[0-9]*"
            control={control}
            defaultValue={0.0}
            fractionDigits={decimalPrecisionSetting}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { height: '40px', fontSize: '13px' },
              inputProps: {
                onFocus: event => setTimeout(() => event.target.focus(), 100),
                step: 0.01,
                min: 0.0,
                readOnly: !inputDisabled,
              },
            }}
          />
        )}
      </PricingTableCell>
      {disableUmDropdowns ? null : (
        <PricingTableCell width="8%" textAlign="center">
          {isViewMode ? (
            <Typography fontSize={rem(14)}>{priceEntryUnits?.toUpperCase()}</Typography>
          ) : (
            <Select
              name={`pricing.${trade}Unit`}
              data-test={`pricing-${trade}Unit`}
              defaultValue={MEASURE_UNIT_ARRAY[0].id}
              control={control}
              items={MEASURE_UNIT_ARRAY}
              selectProps={{
                sx: { height: '40px', fontSize: '13px' },
                onChange: event => {
                  const { value } = event.target
                  setValue(`pricing.buyUnit`, value)
                  setValue(`pricing.sellUnit`, value)
                },
              }}
            />
          )}
        </PricingTableCell>
      )}
      <PricingTableCell width="10%" textAlign="right">
        {isViewMode ? (
          <Typography fontSize={rem(13)}>+/- ${setDecimalPrecision(tradePrice?.minMaxRange)}</Typography>
        ) : (
          <Input
            name={`pricing.${trade}MinMaxRange`}
            data-test={`pricing-${trade}MinMaxRange`}
            type="number"
            pattern="^\d+(\.\d{0,2})?$"
            control={control}
            defaultValue={0.0}
            fractionDigits={decimalPrecisionSetting}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              sx: { height: '40px', fontSize: '13px', minWidth: '100px' },
              inputProps: {
                onFocus: event => setTimeout(() => event.target.focus(), 100),
                step: 0.01,
                min: 0.0,
              },
            }}
          />
        )}
      </PricingTableCell>
    </tr>
  )
}

PricingFormulaRow.propTypes = {
  isViewMode: PropTypes.bool,
  control: PropTypes.object,
  watch: PropTypes.func,
  setValue: PropTypes.func,
  isBuy: PropTypes.bool,
  isPricelist: PropTypes.bool,
  priceEntryUnits: PropTypes.string,
  tradePrice: PropTypes.object,
  materialTypeId: PropTypes.string,
}

PricingFormulaRow.defaultProps = {
  isViewMode: false,
  control: {},
  watch: noop,
  isBuy: false,
  setValue: noop,
  tradePrice: {},
  isPricelist: false,
  priceEntryUnits: '',
  materialTypeId: '',
}

export default PricingFormulaRow
